import { Text } from '@thanx/uikit/text'
import ExternalLink from 'components/Icons/ExternalLink'
import palette from 'constants/palette'
import React from 'react'
import { Link } from 'react-router-dom'
import { useStyletron } from 'styletron-react'

const PANEL_GAP = '24px'
const PANEL_PADDING = '24px'
const PANEL_RADIUS = '8px'
const HEADER_GAP = '16px'
const HEADLINE_LINE_HEIGHT = '28px'
const LINE_GAP = '8px'
const ROW_LINE_HEIGHT = '24px'
const ROW_PADDING = '16px 0'
const FOOTNOTE_LINE_HEIGHT = '20px'
const PANEL_BACKGROUND = palette.grey05
const LINK_LINE_HEIGHT = '20px'

export type MathLine = {
  label: string
  value: string
  /** Deep-link target below the label — an in-app route ("View dashboard") or an
   * absolute Help Center URL ("View source"). Absolute URLs render as a plain
   * <a>; in-app routes as a React Router <Link> (see DeepLink). */
  href?: string
  /** Visible link text (e.g. "View dashboard" / "View source"). */
  linkText?: string
  /** Accessible name for the link — a superset containing `linkText` verbatim
   * (WCAG 2.5.3 Label in Name), e.g. "View dashboard: Purchases report". */
  linkAriaLabel?: string
}

type DeepLinkProps = {
  href: string
  text?: string
  ariaLabel?: string
}

/**
 * A math-row deep-link. Opens in a NEW TAB (target=_blank) so the merchant keeps
 * the promo page open behind it; the trailing ExternalLink glyph is the visible
 * affordance, the "(opens in a new tab)" is folded into `ariaLabel` by the caller.
 * Absolute URLs (the Help Center "View source" links) render as a plain <a> —
 * a React Router <Link> would mistake the full URL for an in-app path; in-app
 * routes render as a <Link> to keep client-side nav.
 */
const DeepLink = ({ href, text, ariaLabel }: DeepLinkProps) => {
  const [css] = useStyletron()
  const className = css({
    alignItems: 'center',
    color: palette.belizeHole,
    display: 'inline-flex',
    gap: '4px',
    lineHeight: LINK_LINE_HEIGHT,
    textDecoration: 'none',
    width: 'fit-content',
  })
  const content = (
    <>
      {text}
      <ExternalLink className={css({ flexShrink: 0 })} />
    </>
  )
  // Absolute http(s) URL ⇒ external host ⇒ plain anchor.
  const isExternal = /^https?:\/\//.test(href)

  if (isExternal) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={ariaLabel}
        className={className}
      >
        {content}
      </a>
    )
  }

  return (
    <Link
      to={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={ariaLabel}
      className={className}
    >
      {content}
    </Link>
  )
}

export type MathRow = {
  /** Paints the grey-20 hairline under the row, per Figma. */
  dividerBelow?: boolean
  /** One entry per label/value pair stacked inside the row. */
  lines: MathLine[]
  /** Grey-90 labels instead of the default grey-70, per Figma. */
  strongLabels?: boolean
}

type Props = {
  eyebrow: string
  /** Optional caption below the total. Omitted for the revenue panel, which
   * carries no footnote per Figma. */
  footnote?: string
  headline: string
  /** Target of the triggering disclosure button's `aria-controls`. */
  id: string
  rows: MathRow[]
  total: MathLine
}

/**
 * The "see the math" breakdown panel that opens under the ROI metric cards.
 *
 * Parameterized for both cards: each takes the same eyebrow/headline/rows/total
 * /footnote shape, so the two Figma variants (labor cost, revenue recovered)
 * differ only in the data passed in.
 */
const SeeTheMathPanel = ({
  eyebrow,
  footnote,
  headline,
  id,
  rows,
  total,
}: Props) => {
  const [css] = useStyletron()

  return (
    <div
      id={id}
      className={css({
        backgroundColor: PANEL_BACKGROUND,
        border: `1px solid ${palette.grey20}`,
        borderRadius: PANEL_RADIUS,
        display: 'flex',
        flexDirection: 'column',
        gap: PANEL_GAP,
        padding: PANEL_PADDING,
        wordBreak: 'break-word',
      })}
    >
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: HEADER_GAP,
        })}
      >
        <Text.BodyText3
          tag="p"
          bold
          color="grey70"
          // Figma renders the eyebrow uppercase — the locale keeps the source
          // casing so translations aren't shouted; text-transform does the rest.
          className={css({
            letterSpacing: '0.3px',
            lineHeight: ROW_LINE_HEIGHT,
            marginBottom: 0,
            textTransform: 'uppercase',
          })}
        >
          {eyebrow}
        </Text.BodyText3>
        <Text.Header3
          tag="p"
          className={css({ lineHeight: HEADLINE_LINE_HEIGHT, marginBottom: 0 })}
        >
          {headline}
        </Text.Header3>
      </div>

      <div className={css({ whiteSpace: 'nowrap' })}>
        {/* Index keys: `rows` is a static ordered list from the caller's module
            scope — never reordered, appended to, or filtered at runtime. */}
        {rows.map((row, index) => (
          <div
            key={index}
            data-testid="math-row"
            className={css({
              alignItems: 'center',
              borderBottom: row.dividerBelow
                ? `1px solid ${palette.grey20}`
                : 'none',
              display: 'flex',
              justifyContent: 'space-between',
              padding: ROW_PADDING,
            })}
          >
            <div
              className={css({
                display: 'flex',
                flexDirection: 'column',
                gap: LINE_GAP,
              })}
            >
              {row.lines.map(line => (
                <React.Fragment key={line.label}>
                  <Text.BodyText3
                    tag="p"
                    bold={row.strongLabels}
                    color={row.strongLabels ? 'grey90' : 'grey70'}
                    className={css({
                      lineHeight: ROW_LINE_HEIGHT,
                      marginBottom: 0,
                    })}
                  >
                    {line.label}
                  </Text.BodyText3>
                  {line.href && (
                    <DeepLink
                      href={line.href}
                      text={line.linkText}
                      ariaLabel={line.linkAriaLabel}
                    />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div
              className={css({
                alignItems: 'flex-end',
                display: 'flex',
                flexDirection: 'column',
                gap: LINE_GAP,
              })}
            >
              {row.lines.map(line => (
                <Text.BodyText3
                  key={line.label}
                  tag="p"
                  bold={row.strongLabels}
                  color="grey90"
                  className={css({
                    lineHeight: ROW_LINE_HEIGHT,
                    marginBottom: 0,
                  })}
                >
                  {line.value}
                </Text.BodyText3>
              ))}
            </div>
          </div>
        ))}

        <div
          className={css({
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            padding: ROW_PADDING,
          })}
        >
          <Text.BodyText3
            tag="p"
            bold
            className={css({ lineHeight: ROW_LINE_HEIGHT, marginBottom: 0 })}
          >
            {total.label}
          </Text.BodyText3>
          <Text.Header3
            tag="p"
            className={css({
              lineHeight: HEADLINE_LINE_HEIGHT,
              marginBottom: 0,
            })}
          >
            {total.value}
          </Text.Header3>
        </div>
      </div>

      {footnote && (
        <Text.BodyText4
          tag="p"
          color="grey70"
          className={css({ lineHeight: FOOTNOTE_LINE_HEIGHT, marginBottom: 0 })}
        >
          {footnote}
        </Text.BodyText4>
      )}
    </div>
  )
}

export default SeeTheMathPanel
